import type {
    PoCheckListResult, PoCheckRow, PoCheckSummary, PoCheckDetail,
    PoCheckFilters, PoCheckSortField, PoReviewStatus,
} from '../types/docuwarePoCheck';

interface ListParams {
    page: number;
    limit: number;
    filters: PoCheckFilters;
    sortBy: PoCheckSortField;
    sortOrder: 'asc' | 'desc';
}

const EMPTY_SUMMARY: PoCheckSummary = {
    verdicts: {}, findingCodes: [], findingCategories: [], companies: [], lastCheckedAt: null,
};

/** Unwrap the house envelope, or throw with the server's own message. */
async function unwrap<T>(res: Response): Promise<{ data: T; total: number }> {
    const body = await res.json();
    if (!res.ok || !body.success) {
        throw new Error(body.error?.message || `API error: ${res.status}`);
    }
    return { data: body.data as T, total: body.pagination?.total ?? 0 };
}

export const docuwarePoCheckApi = {
    /**
     * Raw fetch rather than fetchApi, same reason as the invoice list: the pagination wrapper
     * carries the total and fetchApi unwraps only `data`.
     */
    async list(params: ListParams): Promise<PoCheckListResult> {
        const sp = new URLSearchParams();
        sp.set('page', String(params.page));
        sp.set('limit', String(params.limit));
        sp.set('sortBy', params.sortBy);
        sp.set('sortOrder', params.sortOrder);
        for (const [key, value] of Object.entries(params.filters)) {
            if (value === undefined || value === null || value === '') continue;
            sp.set(key, String(value));
        }

        const res = await fetch(`/api/v1/docuware-invoices/po-checks?${sp}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data, total } = await unwrap<{ items: PoCheckRow[]; summary: PoCheckSummary }>(res);
        return {
            items: data?.items ?? [],
            summary: { ...EMPTY_SUMMARY, ...(data?.summary ?? {}) },
            total,
        };
    },

    async detail(documentId: number): Promise<PoCheckDetail> {
        const res = await fetch(`/api/v1/docuware-invoices/po-checks/${documentId}`, {
            headers: { 'Content-Type': 'application/json' },
        });
        const { data } = await unwrap<PoCheckDetail>(res);
        return data;
    },

    /**
     * Re-check one document. `force` re-reads the PDF instead of reusing the stored
     * transcription - the button for "the extraction looks wrong", and the only path that
     * spends a second model call on the same document.
     *
     * Deliberately NOT routed through the AI gateway (`aiFetch`, deleted in P9 with its last
     * caller): that host has no DocuWare API key,
     * so it would fail at the download rather than at the model. One document measures
     * 17-22 s, inside the SWA budget.
     */
    async run(documentId: number, force = false): Promise<{ checked: number; flagged: number }> {
        const res = await fetch(
            `/api/v1/docuware-invoices/po-checks/${documentId}/run${force ? '?force=true' : ''}`,
            { method: 'POST', headers: { 'Content-Type': 'application/json' } }
        );
        const { data } = await unwrap<{ checked: number; flagged: number }>(res);
        return data;
    },

    async review(
        documentId: number, reviewStatus: PoReviewStatus, reviewComment: string
    ): Promise<void> {
        const res = await fetch(`/api/v1/docuware-invoices/po-checks/${documentId}/review`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reviewStatus, reviewComment }),
        });
        await unwrap<unknown>(res);
    },
};
